import {useQuery} from '@apollo/client';
import {Alert, Spin, Tag} from 'antd';
import moment from 'moment';
import {useSelector} from 'react-redux';
import {makeSelectSubTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';
import {GET_NOTIFICATION_AUDIENCE_SNAPSHOTS} from 'graphql/queries/notifications';

export const InstrumentTargetedTag = () => (
  <Tag color="#8d6cfa" style={{marginLeft: 8, verticalAlign: 'middle'}}>
    Instrument-targeted
  </Tag>
);

const when = value => (value ? moment(value).format('D MMM YYYY, HH:mm') : '—');

const criteriaText = criteria =>
  [
    criteria?.assetClasses?.join(', '),
    criteria?.instruments
      ?.map(instrument => instrument.ticker)
      .filter(Boolean)
      .join(', '),
    criteria?.exchanges?.join(', '),
    criteria?.minHoldingValue &&
      `≥ ${criteria.minHoldingValue.amount.toLocaleString()} ${
        criteria.minHoldingValue.currency
      }`,
    criteria?.includeUnsettled && 'incl. unsettled',
  ]
    .filter(Boolean)
    .join(' · ') || '—';

/** One row per send of a holdings notification, newest first. */
export const HoldingsSnapshots = ({notificationId}) => {
  const canView = Boolean(
    useSelector(state =>
      makeSelectSubTabOperations(
        state,
        NavTabsEnum.NotificationSchedule,
        'sent',
      ),
    )?.view,
  );
  const {data, loading, error} = useQuery(GET_NOTIFICATION_AUDIENCE_SNAPSHOTS, {
    variables: {notificationId},
    skip: !canView,
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });
  const result = data?.getNotificationAudienceSnapshots;

  if (!canView) return null;
  if (loading) return <Spin size="small" />;
  if (error || result?.__typename === 'Error') {
    return (
      <Alert
        type="error"
        showIcon
        message={result?.message || error?.message}
      />
    );
  }
  const snapshots = result?.data ?? [];
  if (!snapshots.length) {
    return <Alert type="info" showIcon message="Not sent to anyone yet." />;
  }
  return (
    <div>
      {snapshots.map(snapshot => (
        <Alert
          key={snapshot.id}
          type={snapshot.projectionStale ? 'warning' : 'success'}
          style={{marginBottom: 8}}
          message={`Sent to ${snapshot.recipientCount.toLocaleString()} users (as of ${when(
            snapshot.asOf,
          )})`}
          description={
            <>
              Resolved {when(snapshot.resolvedAt)}
              {snapshot.recipientsByChannel?.length > 0 &&
                ` · ${snapshot.recipientsByChannel
                  .map(({channel, count}) => `${channel} ${count}`)
                  .join(', ')}`}
              <br />
              Holdings: {criteriaText(snapshot.criteria)}
              {snapshot.projectionStale &&
                ' · holdings data was over 15 minutes old'}
            </>
          }
        />
      ))}
    </div>
  );
};
